import { computed, inject, isSignal, Signal, signal, WritableSignal } from '@angular/core';
import { ROLE_HIERARCHY } from '../../with-role-hierarchy.provider';

function addHierarchyRoles(
  roles: string[] | Set<string>,
  roleHierarchy: {
    [index: string]: string | string[];
  },
): Set<string> {
  const allRoles = new Set<string>(roles);
  const queue = [...roles];

  while (queue.length > 0) {
    const currentRole = queue.shift();
    if (!currentRole) {
      continue;
    }
    const inheritedRoles = roleHierarchy[currentRole];

    if (inheritedRoles) {
      const rolesToAdd = Array.isArray(inheritedRoles) ? inheritedRoles : [inheritedRoles];
      for (const role of rolesToAdd) {
        if (!allRoles.has(role)) {
          allRoles.add(role);
          queue.push(role);
        }
      }
    }
  }

  return allRoles;
}

export const user = <User extends { roles: string[] }>(
  initialValue: User | undefined | Signal<User | null | undefined>,
) => {
  const roleHierarchy = inject(ROLE_HIERARCHY, { optional: true });
  const userSignal = isSignal(initialValue) ? initialValue : signal(initialValue);

  const roles = computed(() => {
    const baseRoles = userSignal()?.roles ?? [];
    if (roleHierarchy) {
      if (Array.isArray(roleHierarchy)) {
        return roleHierarchy.reduce((acc, hierarchy) => {
          return addHierarchyRoles(acc, hierarchy);
        }, new Set(baseRoles));
      }
      return addHierarchyRoles(baseRoles, roleHierarchy);
    }
    return new Set(baseRoles);
  });

  const signalConstruct = userSignal as unknown as WritableSignal<User | null> & {
    hasRole: (target: string) => Signal<boolean>;
    is: (target: string) => Signal<boolean>;
    isNot: (target: string) => Signal<boolean>;
    hasOneOfRoles: (targets: string[]) => Signal<boolean>;
    isOneOf: (targets: string[]) => Signal<boolean>;
    isDisjointFrom: (targets: string[]) => Signal<boolean>;
  };

  signalConstruct.hasRole = (target: string) => computed(() => roles().has(target));
  signalConstruct.is = (target: string) => computed(() => roles().has(target));
  signalConstruct.isNot = (target: string) => computed(() => !roles().has(target));

  signalConstruct.hasOneOfRoles = (targets: string[]) =>
    computed(() => !roles().isDisjointFrom(new Set(targets)));
  signalConstruct.isOneOf = (targets: string[]) =>
    computed(() => !roles().isDisjointFrom(new Set(targets)));
  signalConstruct.isDisjointFrom = (targets: string[]) =>
    computed(() => roles().isDisjointFrom(new Set(targets)));

  return signalConstruct;
};
